import ExitToAppIcon from "@mui/icons-material/OutputRounded";
import ViewWeekOutlined from "@mui/icons-material/ViewWeekOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Badge, Box, Button, Grid, Tooltip, tooltipClasses } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import biApi from "../../../../../../../../api/biApi";
import { useDrillDownExport } from "../../../../hooks/drillDownExport";
import type { DrillDownState } from "../../../../hooks/useDrillDown";
import { selectDrillDownItems } from "../../../../store/selectors/drilldownSelectors";

interface Props {
  state: DrillDownState;
  onShowColumnsOptions: () => void;
  showColumnsCustomBadge?: boolean;
}

export default function ToolBar({ state, onShowColumnsOptions, showColumnsCustomBadge = false }: Props) {
  const exporting = useDrillDownExport(biApi.requestDrillDownExport);
  const configuration = useSelector(selectDrillDownItems);

  const exportAllowed = state.rowsTotal <= 50000;

  const exportNotAllowedTooltipMessage = React.useMemo(() => {
    return exportAllowed
      ? ""
      : "Export supports 50000 entries maximum. Please add conditions to reduce the number of entries and try again.";
  }, [exportAllowed]);

  const exportToExcel = () => {
    if (exportAllowed) {
      exporting.requestExport(state.drillDownConfig);
    }
  };

  if (!configuration || configuration.length === 0) {
    return <></>;
  }

  return (
    <Grid container sx={{ alignItems: "center", gap: 1.5, width: "100%" }}>
      <HorizontalFill />
      <Tooltip
        title={exportNotAllowedTooltipMessage}
        arrow
        slotProps={{
          popper: {
            sx: (theme) => ({
              [`& .${tooltipClasses.arrow}`]: {
                color: theme.palette.common.black,
              },
              [`& .${tooltipClasses.tooltip}`]: {
                backgroundColor: theme.palette.common.black,
              },
            }),
          },
        }}
      >
        <Box>
          <Button
            variant="outlined"
            color="secondary"
            disabled={exporting.exporting}
            startIcon={
              exportAllowed ? (
                <ExitToAppIcon />
              ) : (
                <WarningAmberRoundedIcon sx={(t) => ({ color: t.palette.warning.light })} />
              )
            }
            loading={exporting.exporting}
            onClick={exportToExcel}
          >
            Export
          </Button>
        </Box>
      </Tooltip>
      <Badge color="info" variant="dot" invisible={!showColumnsCustomBadge}>
        <Button variant="outlined" color="secondary" startIcon={<ViewWeekOutlined />} onClick={onShowColumnsOptions}>
          Column options
        </Button>
      </Badge>
    </Grid>
  );
}
